import React, { useState, useEffect, useCallback } from "react";
import { Info } from "lucide-react";
import api from "config/axiosConfig";
import { InputOTP, InputOTPGroup, InputOTPSeparator, InputOTPSlot } from "ui/InputOTP";
import { useUser } from "util/UserContext";
import { REGEXP_ONLY_DIGITS } from "input-otp";
import InputField from "ui/InputField";
import AuthService from "services/authService";
import { otpRequestSchema, otpVerifySchema } from "lib/validation/authSchemas";
import { getApiErrorMessage } from "lib/errors/normalizeApiError";
import { apiRoutes } from "util/apiRoutes";
import { getAuthUiStrings, resolveAuthLocale } from "i18n/authUi";

/**
 * Stable form ids used to pair the OTP send-code and verify-code steps
 * with the parent AuthModal's footer submit button. Each step lives in
 * its own `<form>` so browser-native Enter-key submit triggers the
 * correct action per step.
 *
 * See `docs/architecture/forms-standard.md` section 2.
 */
export const OTP_LOGIN_SEND_FORM_ID = "auth-otp-login-send-form";
export const OTP_LOGIN_VERIFY_FORM_ID = "auth-otp-login-verify-form";

interface OTPLoginProps {
  onClose: () => void;
  onCodeSentChange?: (emailSent: boolean) => void;
  onStateChange?: (state: {
    isSubmitting: boolean;
    isButtonDisabled: boolean;
    buttonLabel: string;
  }) => void;
}

/**
 * OTPLogin
 *
 * Content-only auth step for requesting and submitting an OTP sign-in
 * code. The surrounding modal chrome is owned by `AuthModal`. Each step
 * is wrapped in a `<form id>` so the AuthModal footer's
 * `<FormActions formId=...>` triggers it via browser-native submit.
 */
const OTPLogin: React.FC<OTPLoginProps> = ({ onClose, onCodeSentChange, onStateChange }) => {
  const [verificationCode, setVerificationCode] = useState<string>("");
  const { refreshUser } = useUser();
  const ui = getAuthUiStrings(resolveAuthLocale());
  const [emailOrUsername, setEmailOrUsername] = useState("");
  const [emailSent, setEmailSent] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSendingCode, setIsSendingCode] = useState(false);
  const [resendTimer, setResendTimer] = useState(0);
  const [isResending, setIsResending] = useState(false);

  useEffect(() => {
    if (onCodeSentChange) {
      onCodeSentChange(emailSent);
    }
  }, [emailSent, onCodeSentChange]);

  useEffect(() => {
    if (onStateChange) {
      onStateChange({
        isSubmitting: isSendingCode || isSubmitting,
        isButtonDisabled: emailSent
          ? verificationCode.length !== 6 || isSubmitting
          : emailOrUsername.trim().length < 2 || isSendingCode,
        // Stable label per active step. Loading state is rendered as
        // a centered spinner by FormActions in the AuthModal footer
        // (see `docs/architecture/forms-standard.md` section 4 on
        // primary-button consistency), so we never send a "...ing"
        // variant here -- doing so would double up with the spinner.
        buttonLabel: emailSent ? ui.signInCta : ui.sendCodeCta,
      });
    }
  }, [
    emailOrUsername,
    emailSent,
    isSendingCode,
    isSubmitting,
    onStateChange,
    verificationCode.length,
    ui.signInCta,
    ui.sendCodeCta,
  ]);

  useEffect(() => {
    let timer: ReturnType<typeof setInterval> | undefined;
    if (resendTimer > 0) {
      timer = setInterval(() => {
        setResendTimer((prev) => prev - 1);
      }, 1000);
    }
    return () => {
      if (timer) clearInterval(timer);
    };
  }, [resendTimer]);

  const handleLogin = useCallback(async () => {
    const verifyResult = otpVerifySchema.safeParse({ verificationCode });
    if (!verifyResult.success) {
      setError(verifyResult.error.issues[0]?.message || ui.emailVerificationFailed);
      return;
    }
    setError(null);
    setIsSubmitting(true);
    try {
      const response = await api.post(apiRoutes.auth.signinOtp, { code: verificationCode });
      if (response.status === 200) {
        if (response.data.accessToken) {
          AuthService.setAccessToken(response.data.accessToken);
        }
        await refreshUser();
        onClose();
      }
    } catch (err) {
      // Prefers backend response.data.message over axios's technical
      // "Request failed with status code N" default. See
      // `lib/errors/normalizeApiError`.
      setError(getApiErrorMessage(err, ui.emailVerificationFailed));
      setVerificationCode("");
    } finally {
      setIsSubmitting(false);
    }
  }, [verificationCode, onClose, refreshUser, ui.emailVerificationFailed]);

  // Auto-submit when code is complete (preserves existing UX).
  useEffect(() => {
    if (verificationCode.length === 6 && !isSubmitting) {
      handleLogin();
    }
  }, [verificationCode, handleLogin, isSubmitting]);

  const handleEmailConfirm = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEmailOrUsername(e.target.value);
  };

  const handleSendCode = useCallback(async () => {
    const trimmedEmailOrUsername = emailOrUsername.trim();

    const requestResult = otpRequestSchema.safeParse({ emailOrUsername: trimmedEmailOrUsername });
    if (!requestResult.success) {
      setError(requestResult.error.issues[0]?.message || ui.authFailedFallback);
      return;
    }

    if (emailSent && (resendTimer > 0 || isResending || isSendingCode)) {
      return;
    }

    setIsSendingCode(true);
    setError(null);
    try {
      await api.post(apiRoutes.auth.passcode, {
        emailOrUsername: trimmedEmailOrUsername,
        codeType: "otp_login",
      });
      setEmailSent(true);
      setResendTimer(30);
    } catch (err) {
      setError(getApiErrorMessage(err, ui.emailVerificationResendFailed));
    } finally {
      setIsSendingCode(false);
    }
  }, [emailOrUsername, emailSent, isResending, isSendingCode, resendTimer, ui.authFailedFallback, ui.emailVerificationResendFailed]);

  const handleResend = async () => {
    if (resendTimer > 0 || isResending || isSendingCode) {
      return;
    }

    setIsResending(true);
    setError(null);
    try {
      await api.post(apiRoutes.auth.passcode, {
        emailOrUsername: emailOrUsername.trim(),
        codeType: "otp_login",
      });
      setResendTimer(30);
    } catch (err) {
      setError(getApiErrorMessage(err, ui.emailVerificationResendFailed));
    } finally {
      setIsResending(false);
    }
  };

  /** Form-submit handler for the email step. */
  const handleSendFormSubmit = (e?: React.FormEvent) => {
    if (e && typeof e.preventDefault === "function") {
      e.preventDefault();
    }
    void handleSendCode();
  };

  /** Form-submit handler for the verify-code step. */
  const handleVerifyFormSubmit = (e?: React.FormEvent) => {
    if (e && typeof e.preventDefault === "function") {
      e.preventDefault();
    }
    void handleLogin();
  };

  return (
    <div className="otp-login-modal-content flex flex-col w-full space-y-4">
      <div className="flex flex-col space-y-4">
        {!emailSent ? (
          <form id={OTP_LOGIN_SEND_FORM_ID} onSubmit={handleSendFormSubmit} noValidate>
            <div className="form-control flex flex-col mb-0 space-y-4">
              <p className="text-sm text-eoe-text-sub dark:text-eoe-dark-text-sub">
                {ui.otpEmailStepInstruction}
              </p>
              <InputField
                type="email"
                id="emailOrUsername"
                name="emailOrUsername"
                labelText={ui.emailOrUsernameLabel}
                autoComplete="username"
                fieldRequired
                value={emailOrUsername}
                onChange={handleEmailConfirm}
                validationState={emailOrUsername.length >= 2 ? "valid" : emailOrUsername.length > 0 ? "invalid" : "none"}
                spellCheck={false}
              />

              {error && (
                <div className="flex items-center text-warning-red dark:text-warning-red-dark text-sm" role="alert">
                  <Info className="mr-1 size-4" />
                  <span>{error}</span>
                </div>
              )}
            </div>
          </form>
        ) : (
          <form id={OTP_LOGIN_VERIFY_FORM_ID} onSubmit={handleVerifyFormSubmit} noValidate>
            <div className="flex flex-col space-y-4">
              <p className="text-sm text-eoe-text-sub dark:text-eoe-dark-text-sub">
                {ui.otpCodeStepInstruction}
              </p>
              <div className="otp-login-modal-input-otp flex justify-center">
                <InputOTP
                  maxLength={6}
                  pattern={REGEXP_ONLY_DIGITS}
                  inputMode="numeric"
                  value={verificationCode}
                  autoFocus={true}
                  onChange={(value) => {
                    setError(null);
                    setVerificationCode(value);
                  }}
                  containerClassName="group flex items-center"
                  disabled={isSubmitting}
                >
                  <InputOTPGroup>
                    <InputOTPSlot index={0} />
                    <InputOTPSlot index={1} />
                    <InputOTPSlot index={2} />
                  </InputOTPGroup>
                  <InputOTPSeparator />
                  <InputOTPGroup>
                    <InputOTPSlot index={3} />
                    <InputOTPSlot index={4} />
                    <InputOTPSlot index={5} />
                  </InputOTPGroup>
                </InputOTP>
              </div>

              {error && (
                <div className="flex items-center text-warning-red dark:text-warning-red-dark text-sm" role="alert">
                  <Info className="mr-1 size-4" />
                  <span>{error}</span>
                </div>
              )}

              <div className="flex flex-col items-center space-y-2 sm:items-start">
                <div className="text-center sm:justify-start">
                  <span className="text-sm text-eoe-text-sub dark:text-eoe-dark-text-sub">
                    {ui.emailVerificationResendLead}{" "}
                    {resendTimer > 0 ? (
                      <span>
                        {ui.emailVerificationResendIn.replace(
                          "{time}",
                          `${Math.floor(resendTimer / 60)}:${(resendTimer % 60).toString().padStart(2, "0")}`
                        )}
                      </span>
                    ) : (
                      <button
                        type="button"
                        onClick={handleResend}
                        disabled={isResending}
                        className="text-eoe-link-blue dark:text-eoe-dark-link-blue hover:underline disabled:opacity-50"
                      >
                        {ui.emailVerificationResend}
                      </button>
                    )}
                  </span>
                </div>
              </div>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default OTPLogin;
